<template>
  <div ref="elRef" :class="['my-app-card-list-root', !props.isFold && 'full']">
    <my-app-card-item
      v-for="(app, index) in innerAppList"
      :key="app.platform_id + '_' + app.third_app_id + '_' + index"
      :isShowWorkbench="props.isShowWorkbench"
      :appData="app"
      :activeAppId="activeAppId"
      @on-reload-data="onReloadData"
      @show-app-pay-require-dialog="showAppPayRequireDialog"
    ></my-app-card-item>
  </div>

  <my-app-pay-tips-dialog
    v-if="isShowPayTipsDialog"
    v-bind="appPayTipData"
    @on-app-pay-tips-tips-modal-closed="isShowPayTipsDialog = false"
  ></my-app-pay-tips-dialog>

  <!-- <my-app-pay v-if="isPayModalShow" v-bind="appFullData"></my-app-pay> -->

  <app-pay
    v-if="isAppPayShow"
    :appData="appFullData?.appData"
    :appRightsData="appFullData?.appRightsData"
    :isPayApp="appPayData?.isPayApp"
    :payConfig="appPayData?.payConfig"
    :fromComp="fromComp"
    :isAddMember="isAddMember"
    :isPayWorkspaceOnly="isPayWorkspaceOnly"
    :isRenewPlatform="isRenewPlatform"
    @on-app-pay2t-dialog-closed="onAppPay2tClosed"
    @on-reload-data="onReloadData"
  ></app-pay>

  <my-app-del
    v-if="isDelModalShow"
    v-bind="appFullData"
    @on-del-app-dialog-closed="hideDelAppDialog"
    @on-reload-data="onReloadData"
  ></my-app-del>
</template>

<script lang="ts" setup>
import { RespApp } from '@vas2t-lowcode/api';
import { useResizeObserver } from '@vueuse/core';
import {
  computed,
  defineAsyncComponent,
  defineProps,
  inject,
  onBeforeUnmount,
  onMounted,
  PropType,
  ref,
  watch,
} from 'vue';
import type { IAppPayData, IMyAppFullData, IMyAppPropData } from './my-app';
import MyAppCardItem from './my-app-card-item.vue';
import { CARD_SIZE_SPACE, CARD_SIZE_WIDTH } from './my-app.constants';
import { destroyMyAppEmitter, getMyAppEmitter, MyAppEmitterKey } from './my-app.tools';
import { PAY_TYPE } from './use-app-pay';

const props = defineProps({
  /** @type {Array}  应用列表 */
  appList: {
    type: Array as PropType<RespApp[]>,
    default() {
      return [] as RespApp[];
    },
  },
  /** @type {boolean} 是否折叠状态 */
  isFold: {
    type: Boolean,
    default: true,
  },
  /** 是否显示工作区-用于工作区所有应用 */
  isShowWorkbench: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  /** 通知父组件只显示一行 */
  (e: 'on-is-two-row-app-list', value: boolean): void;
  /** 通知父组件重新加载数据 */
  (e: 'on-reload-my-app-list'): void;
}>();

/** 当前显示的 appList */
const innerAppList = ref<RespApp[]>([]);

const elRef = ref<HTMLElement | null>(null);

/** 监测窗体大小变化，更新 appList */
let resizeObAppList: ReturnType<typeof useResizeObserver> | undefined;
const stopResizeObAppList = () => {
  resizeObAppList?.stop();
};

/** 默认展示2行数据 */
const DEF_ROW_SIZE = 2;
/** 设置 innerAppList */
const setInnerAppList = () => {
  const { isFold, appList } = props;
  if (!isFold) {
    innerAppList.value = appList || [];
  }

  // 检查是否所有的列表
  const rootEl = elRef.value;
  const width = (rootEl && getComputedStyle(rootEl).width + '') || '0px';
  // 计算一行能放置多少卡片
  const num = Math.floor(parseInt(width) / (CARD_SIZE_WIDTH + CARD_SIZE_SPACE));
  isFold && (innerAppList.value = appList.slice(0, num * DEF_ROW_SIZE));

  // 判断是否只显示两行数据
  emit('on-is-two-row-app-list', num * 2 >= appList.length);
};

// 观察应用数量变更，更新innerAppList
watch(
  () => props.appList,
  () => {
    stopResizeObAppList();
    resizeObAppList = useResizeObserver(elRef, () => {
      setInnerAppList();
    });
  },
  { immediate: true, deep: true }
);

// 观察应用数量变更，更新innerAppList
watch(
  () => props.isFold,
  () => {
    setInnerAppList();
  }
);

/** 用于标记支付显示的位置 */
const fromComp = ref<string | undefined>(undefined);

const activeAppData = ref<IMyAppPropData>({} as IMyAppPropData);

const activeAppId = computed(() => {
  return activeAppData.value?.appData?.third_app_id || '';
});

const appPayTipData = ref<IMyAppFullData>();
const showAppPayRequireDialog = (data: IMyAppFullData) => {
  isShowPayTipsDialog.value = true;
  appPayTipData.value = data;
};
// 显示支付提示弹窗
const myAppPayTipsDialog = defineAsyncComponent(() => import('./my-app-pay-tips-dialog.vue'));
const isShowPayTipsDialog = ref(false);

const appFullData = ref<IMyAppFullData>();

const emitterName = inject<MyAppEmitterKey>('emitterName')!;
/** 绑定监听pay modal关闭 */
const emitter = getMyAppEmitter(emitterName);
const bindEmitter = () => {
  emitter.on('on-my-app-del-dialog-show', showAppDelDialog);
  emitter.on('on-my-app-pay-closed', hideAppPayDialog);
  emitter.on('on-my-app-pay-show', showAppPayDialog);
  emitter.on('2t-pay-show', showAppPay);
};

const destroyEmitter = () => {
  destroyMyAppEmitter(emitterName);
};
onBeforeUnmount(() => {
  destroyEmitter();
});

// #region myAppPay 应用支付弹窗
const isAddMember = ref(false);
const isPayModalShow = ref(false);
// 单购用户点击增加成员，显示纯平台支付
const isPayWorkspaceOnly = ref(false);
const hideAppPayDialog = () => {
  isPayModalShow.value = false;
};

const isRenewPlatform = ref(false);

const showAppPayDialog = (data: IMyAppFullData) => {
  appFullData.value = data;
  fromComp.value = data.fromComp;
  isPayModalShow.value = true;
};
// #endregion

// #region 2t AppPay 弹窗
const appPay = defineAsyncComponent(() => import('@/features/app-pay/app-pay.vue'));

/** 显示应用支付 */
const isAppPayShow = ref(false);

const appPayData = ref<IAppPayData>();
const showAppPay = (data: IAppPayData) => {
  appFullData.value = {
    appData: data.appData,
    appRightsData: data.appRightsData,
  };
  // 判断是否新增成员
  isAddMember.value = data.payType === PAY_TYPE.MyAppMemPay;
  isPayWorkspaceOnly.value = data.payType === PAY_TYPE.NewWorkspacePay;
  isRenewPlatform.value = data.payType === PAY_TYPE.WorkspacePay;
  appPayData.value = data;
  fromComp.value = data.fromComp;
  isAppPayShow.value = true;
};

const onAppPay2tClosed = () => {
  isAppPayShow.value = false;
};
// #endregion

// #region 删除应用弹窗
const myAppDel = defineAsyncComponent(() => import('./my-app-del.vue'));

const isDelModalShow = ref(false);
const showAppDelDialog = (data: IMyAppFullData) => {
  appFullData.value = data;
  isDelModalShow.value = true;
};
const hideDelAppDialog = () => {
  isDelModalShow.value = false;
};
// #endregion

const onReloadData = () => {
  emit('on-reload-my-app-list');
};

onMounted(() => {
  bindEmitter();
});
</script>

<style lang="scss" scoped>
.my-app-card-list {
  &-root {
    box-sizing: border-box;
    display: flex;
    padding: 0px 4px 0px 4px;
    min-height: 100px;
    overflow: hidden;
    flex-wrap: wrap;

    &.full {
      height: auto;
    }
  }
}
</style>
